import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { purchaseApi, vendorApi } from "../services/api";
import { useAuth } from "../context/AuthContext";
import Modal from "../components/Modal";
import {
  FiArrowLeft,
  FiPrinter,
  FiEdit,
  FiTrash2,
  FiFileText,
  FiTruck,
  FiCalendar,
  FiUser,
  FiCheckCircle,
  FiAlertTriangle,
  FiBox,
  FiShield,
  FiTag,
  FiRotateCcw,
  FiLayers,
  FiCheck,
} from "react-icons/fi";
import { toast } from "react-toastify";
import logoImg from "../assets/Icons/Logo.png";

// Helper to convert number to words for invoice total
const numberToWords = (num) => {
  if (num === null || num === undefined || isNaN(num)) return "";
  const a = [
    "", "One ", "Two ", "Three ", "Four ", "Five ", "Six ", "Seven ", "Eight ", "Nine ",
    "Ten ", "Eleven ", "Twelve ", "Thirteen ", "Fourteen ", "Fifteen ", "Sixteen ", "Seventeen ", "Eighteen ", "Nineteen "
  ];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const n = ("000000000" + Math.floor(Math.abs(num))).substr(-9);
  const match = n.match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
  if (!match) return "";

  let str = "";
  str += Number(match[1]) !== 0 ? (a[Number(match[1])] || `${b[match[1][0]]} ${a[match[1][1]]}`) + "Crore " : "";
  str += Number(match[2]) !== 0 ? (a[Number(match[2])] || `${b[match[2][0]]} ${a[match[2][1]]}`) + "Lakh " : "";
  str += Number(match[3]) !== 0 ? (a[Number(match[3])] || `${b[match[3][0]]} ${a[match[3][1]]}`) + "Thousand " : "";
  str += Number(match[4]) !== 0 ? (a[Number(match[4])] || `${b[match[4][0]]} ${a[match[4][1]]}`) + "Hundred " : "";
  str += Number(match[5]) !== 0
    ? (str !== "" ? "and " : "") + (a[Number(match[5])] || `${b[match[5][0]]} ${a[match[5][1]]}`)
    : "";

  return str.trim() ? `Rupees ${str.trim()} Only` : "Rupees Zero Only";
};

const PurchaseInvoicePage = () => {
  const { billNumber } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [bill, setBill] = useState(location.state?.bill || null);
  const [loading, setLoading] = useState(!location.state?.bill);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // 2 Options:
  // 1) "no_return"   -> Purchase Invoice Without Return (Original Purchase Bill as received without return rows/deductions)
  // 2) "with_return" -> Purchase Invoice With Return (Adjusted Purchase Bill showing returned items, return vouchers, deductions, and net retained items)
  const queryParams = new URLSearchParams(location.search);
  const templateFromQuery = queryParams.get("template") || queryParams.get("tpl");

  const [selectedTemplate, setSelectedTemplate] = useState(() => {
    if (templateFromQuery === "dc_no_return" || templateFromQuery === "dc") return "dc_no_return";
    if (templateFromQuery === "with_return") return "with_return";
    if (templateFromQuery === "no_return" || templateFromQuery === "detail") return "no_return";
    const saved = localStorage.getItem("purchase_invoice_selected_template");
    if (saved === "dc_no_return" || saved === "dc") return "dc_no_return";
    if (saved === "with_return") return "with_return";
    return "no_return";
  });

  const handleTemplateChange = (tplId) => {
    setSelectedTemplate(tplId);
    localStorage.setItem("purchase_invoice_selected_template", tplId);
  };

  const canEdit = user?.role === "admin" || (Array.isArray(user?.permissions) && user.permissions.includes("purchases"));

  const handleEditBill = (targetBill = bill) => {
    if (!targetBill) return;
    navigate(`/purchases?tab=record&editBill=${encodeURIComponent(targetBill.bill_number)}`, {
      state: { bill: targetBill },
    });
  };

  const fetchBillDetails = useCallback(async () => {
    try {
      setLoading(true);
      const decodedBillNo = decodeURIComponent(billNumber);
      const res = await purchaseApi.getById(decodedBillNo);
      if (res.data) {
        const p = res.data;
        const activeItemsSum = (p.items || []).reduce(
          (acc, it) => acc + (parseFloat(it.price || it.unit_price) || 0) * (parseInt(it.quantity) || 1),
          0
        );
        const totalAmt = activeItemsSum > 0 ? activeItemsSum : (parseFloat(p.total_amount) || 0);

        const loadedBill = {
          ...p,
          total_price: totalAmt,
          items: p.items || [],
          return_items: p.return_items || [],
          returns: p.returns || [],
        };
        setBill(loadedBill);

        // Check query params if opened with ?edit=true -> redirect to record tab
        const params = new URLSearchParams(location.search);
        if (params.get("edit") === "true") {
          navigate(`/purchases?tab=record&editBill=${encodeURIComponent(loadedBill.bill_number)}`, {
            replace: true,
            state: { bill: loadedBill },
          });
        }
      }
    } catch (err) {
      console.error("Failed to fetch bill details:", err);
      // If we don't already have bill from state, report error
      if (!location.state?.bill) {
        toast.error(err.response?.data?.error || err.message || "Failed to load invoice details");
      }
    } finally {
      setLoading(false);
    }
  }, [billNumber, location.state?.bill, location.search]);

  useEffect(() => {
    fetchBillDetails();
  }, [fetchBillDetails]);

  const handlePrint = () => {
    window.print();
  };

  const handleDeleteBill = async () => {
    if (!bill) return;
    try {
      setDeleting(true);
      const idToDelete = bill.id || bill.bill_number;
      const res = await purchaseApi.delete(idToDelete);
      toast.success(res.data?.message || `Purchase bill #${bill.bill_number} and all related items deleted`);
      setDeleteModalOpen(false);
      navigate("/purchases?tab=history");
    } catch (err) {
      console.error("Delete bill error:", err);
      toast.error(err.response?.data?.error || err.message || "Failed to delete purchase bill");
    } finally {
      setDeleting(false);
    }
  };

  const formatRs = (amt) => {
    const n = parseFloat(amt) || 0;
    return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "—";
    try {
      if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
        const parts = dateStr.slice(0, 10).split("-");
        const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
        return d.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return String(dateStr);
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return String(dateStr);
    }
  };

  const billItems = bill?.items || [];

  // Consolidate bill items:
  // 1) Non-serial bulk items that were partially sold are grouped together into their original purchase line
  //    (e.g., 120 purchased: 70 unsold + 50 sold -> shown as 120 purchased, not 2 broken rows!)
  // 2) Attach return records for each item from bill.return_items
  const consolidatedItems = React.useMemo(() => {
    const rawItems = bill?.items || [];
    const returnItems = bill?.return_items || [];

    const map = new Map();

    rawItems.forEach((it) => {
      const isSerial = Boolean(it.serial_number && String(it.serial_number).trim() !== "");
      const cleanSn = isSerial ? String(it.serial_number).trim() : "";
      const rawItemName = (it.item_name || "").trim();
      const rawCategory = (it.category_name || "").trim();
      const rawSubcat = (it.subcategory_name || "").trim();
      const rawPartNo = (it.part_number || it.sku || "").trim();
      const unitCost = parseFloat(it.price || it.unit_price) || 0;

      // Group key: serial items have unique key; non-serial items group by item_name, category, part_number, unitCost
      const key = isSerial
        ? `sn_${cleanSn.toLowerCase()}`
        : `bulk_${rawItemName}_${it.category_id || rawCategory}_${it.sub_category_id || rawSubcat}_${rawPartNo}_${unitCost}`.toLowerCase();

      const qty = parseInt(it.quantity, 10) || 1;
      const isSold = Boolean(it.is_sale);

      if (!map.has(key)) {
        map.set(key, {
          key,
          id: it.id,
          item_name: rawItemName,
          category_name: it.category_name,
          subcategory_name: it.subcategory_name,
          serial_number: cleanSn || null,
          part_number: it.part_number || null,
          sku: it.sku || null,
          unit_price: unitCost,
          warranty_start_date: it.warranty_start_date,
          warranty_end_date: it.warranty_end_date,
          warranty_status: it.warranty_status,
          is_serial: isSerial,
          // Quantities
          activeQuantity: 0,
          soldQuantity: 0,
          instockQuantity: 0,
          returnedQuantity: 0,
          returnedAmount: 0,
          returnsList: [],
        });
      }

      const grp = map.get(key);
      grp.activeQuantity += qty;
      if (isSold) {
        grp.soldQuantity += qty;
      } else {
        grp.instockQuantity += qty;
      }
    });

    // Match return items under this bill
    returnItems.forEach((ret) => {
      const isSerial = Boolean(ret.serial_number && String(ret.serial_number).trim() !== "");
      const cleanSn = isSerial ? String(ret.serial_number).trim() : "";
      const retName = (ret.item_name || "").trim().toLowerCase();
      const retQty = parseInt(ret.quantity, 10) || 1;
      const retAmt = parseFloat(ret.total_price) || (retQty * (parseFloat(ret.unit_price) || 0));

      let matched = false;
      if (isSerial) {
        const key = `sn_${cleanSn.toLowerCase()}`;
        if (map.has(key)) {
          const grp = map.get(key);
          grp.returnedQuantity += retQty;
          grp.returnedAmount += retAmt;
          grp.returnsList.push(ret);
          matched = true;
        }
      } else {
        for (const grp of map.values()) {
          if (!grp.is_serial && grp.item_name.toLowerCase() === retName) {
            grp.returnedQuantity += retQty;
            grp.returnedAmount += retAmt;
            grp.returnsList.push(ret);
            matched = true;
            break;
          }
        }
      }

      if (!matched) {
        const retKey = `orphaned_return_${ret.id}`;
        map.set(retKey, {
          key: retKey,
          id: ret.id,
          item_name: ret.item_name,
          category_name: ret.category_name || "—",
          subcategory_name: ret.subcategory_name || "",
          serial_number: cleanSn || null,
          part_number: ret.part_number || null,
          sku: ret.sku || null,
          unit_price: parseFloat(ret.unit_price) || 0,
          warranty_start_date: null,
          warranty_end_date: null,
          warranty_status: null,
          is_serial: isSerial,
          activeQuantity: 0,
          soldQuantity: 0,
          instockQuantity: 0,
          returnedQuantity: retQty,
          returnedAmount: retAmt,
          returnsList: [ret],
        });
      }
    });

    return Array.from(map.values()).map((grp) => {
      const originalPurchasedQty = grp.activeQuantity + grp.returnedQuantity;
      const originalGrossAmt = originalPurchasedQty * grp.unit_price;
      const remainingQty = grp.activeQuantity;
      const remainingAmt = remainingQty * grp.unit_price;

      return {
        ...grp,
        originalPurchasedQty,
        originalGrossAmt,
        remainingQty,
        remainingAmt,
      };
    });
  }, [bill?.items, bill?.return_items]);

  const summaryTotals = React.useMemo(() => {
    let totalPurchasedUnits = 0;
    let totalPurchasedAmount = 0;
    let totalReturnedUnits = 0;
    let totalReturnedAmount = 0;
    let totalRemainingUnits = 0;
    let totalRemainingAmount = 0;

    consolidatedItems.forEach((it) => {
      totalPurchasedUnits += it.originalPurchasedQty;
      totalPurchasedAmount += it.originalGrossAmt;
      totalReturnedUnits += it.returnedQuantity;
      totalReturnedAmount += it.returnedAmount;
      totalRemainingUnits += it.remainingQty;
      totalRemainingAmount += it.remainingAmt;
    });

    const headerReturnedAmt = parseFloat(bill?.returned_amount) || 0;
    if (totalReturnedAmount === 0 && headerReturnedAmt > 0) {
      totalReturnedAmount = headerReturnedAmt;
      totalRemainingAmount = Math.max(0, totalPurchasedAmount - totalReturnedAmount);
    }

    if (totalPurchasedAmount === 0) {
      totalPurchasedAmount = parseFloat(bill?.gross_amount || bill?.total_amount || 0);
      totalRemainingAmount = parseFloat(bill?.total_amount || bill?.total_price || 0);
    }

    return {
      totalPurchasedUnits,
      totalPurchasedAmount,
      totalReturnedUnits,
      totalReturnedAmount,
      totalRemainingUnits,
      totalRemainingAmount,
    };
  }, [consolidatedItems, bill?.returned_amount, bill?.gross_amount, bill?.total_amount, bill?.total_price]);


  // DC Invoice: Group items by category & item name with comma-separated serial numbers
  const dcInvoiceItems = React.useMemo(() => {
    const map = {};
    consolidatedItems.forEach((it) => {
      const rawCatName = (it.category_name || it.subcategory_name || "").trim() || "General / IT Hardware";
      const rawItemName = (it.item_name || "Item").trim();
      const rawPartNo = (it.part_number || it.sku || "").trim();
      const groupKey = `${rawCatName}___${rawItemName}`.toLowerCase();

      if (!map[groupKey]) {
        map[groupKey] = {
          categoryName: rawCatName,
          itemName: rawItemName,
          partNumber: rawPartNo,
          originalQty: 0,
          originalAmount: 0,
          returnedQty: 0,
          returnedAmount: 0,
          remainingQty: 0,
          remainingAmount: 0,
          returnsList: [],
          serials: [],
          returnedSerials: [],
        };
      }
      const grp = map[groupKey];
      if (!grp.partNumber && rawPartNo) grp.partNumber = rawPartNo;
      grp.originalQty += it.originalPurchasedQty;
      grp.originalAmount += it.originalGrossAmt;
      grp.returnedQty += it.returnedQuantity;
      grp.returnedAmount += it.returnedAmount;
      grp.remainingQty += it.remainingQty;
      grp.remainingAmount += it.remainingAmt;
      if (it.returnsList && it.returnsList.length > 0) {
        grp.returnsList.push(...it.returnsList);
      }
      if (it.serial_number) {
        if (it.returnedQuantity > 0 && it.activeQuantity === 0) {
          if (!grp.returnedSerials.includes(it.serial_number)) {
            grp.returnedSerials.push(it.serial_number);
          }
        } else {
          if (!grp.serials.includes(it.serial_number)) {
            grp.serials.push(it.serial_number);
          }
        }
      }
    });
    return Object.values(map);
  }, [consolidatedItems]);

  const totalPrice = summaryTotals.totalRemainingAmount;
  const totalUnits = summaryTotals.totalPurchasedUnits;

  if (loading && !bill) {
    return (
      <div style={{ padding: "60px 20px", textAlign: "center", color: "var(--slate-500)" }}>
        <div style={{ fontSize: "1.2rem", fontWeight: 700, marginBottom: 8, color: "var(--navy)" }}>
          Loading Purchase Invoice...
        </div>
        <p>Fetching full details and itemized inventory records.</p>
      </div>
    );
  }

  if (!bill) {
    return (
      <div style={{ padding: "60px 20px", textAlign: "center" }}>
        <h2 style={{ color: "var(--navy)", marginBottom: 12 }}>Invoice Not Found</h2>
        <p style={{ color: "var(--slate-500)", marginBottom: 24 }}>
          Could not find purchase bill #{decodeURIComponent(billNumber || "")}.
        </p>
        <button
          type="button"
          onClick={() => navigate("/purchases?tab=history")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 20px",
            backgroundColor: "var(--primary)",
            color: "#fff",
            borderRadius: 8,
            border: "none",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          <FiArrowLeft size={16} /> Back to Purchase History
        </button>
      </div>
    );
  }

  // =========================================================================
  // PURCHASE INVOICE TEMPLATE (Supports withReturn = true / false)
  // 1) withReturn = false -> Original bill without return rows/deductions
  // 2) withReturn = true  -> Adjusted bill showing returns, deductions, net active items
  // =========================================================================
  const renderPurchaseInvoiceTemplate = (withReturn = false) => {
    const activeTotal = withReturn ? summaryTotals.totalRemainingAmount : summaryTotals.totalPurchasedAmount;
    const activeUnits = withReturn ? summaryTotals.totalRemainingUnits : summaryTotals.totalPurchasedUnits;

    return (
      <div
        className="invoice-paper"
        style={{
          maxWidth: 1000,
          margin: "0 auto",
          backgroundColor: "#fff",
          borderRadius: 14,
          border: "1px solid var(--slate-200)",
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
          padding: "44px 48px",
          color: "var(--slate-800)",
        }}
      >
        {/* INVOICE HEADER */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            borderBottom: "2.5px solid var(--primary, #064E3B)",
            paddingBottom: 24,
            marginBottom: 28,
            gap: 20,
            flexWrap: "wrap",
          }}
        >
          {/* Company Branding */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img
                src={logoImg}
                alt="Commercial IT Logo"
                style={{
                  height: 60,
                  maxHeight: 60,
                  width: "auto",
                  objectFit: "contain",
                  borderRadius: 6,
                }}
              />

            </div>
          </div>

          {/* Invoice Document Title & Bill No */}
          <div style={{ textAlign: "right", minWidth: 220 }}>
            <div
              style={{
                fontSize: "1.4rem",
                fontWeight: 900,
                color: "var(--navy)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Bill# {bill.bill_number}
            </div>



          </div>
        </div>

        {/* METADATA SECTION: VENDOR & TRANSACTION DETAILS (2 Column Layout) */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 20,
            marginBottom: 28,
          }}
        >
          {/* Card 1: Vendor / Supplier Info */}
          <div
            style={{
              padding: "18px 20px",
              borderRadius: 10,
              backgroundColor: "#F8FAFC",
              border: "1px solid var(--slate-200)",
            }}
          >
            <div
              style={{
                fontSize: "0.72rem",
                fontWeight: 800,
                color: "var(--primary, #064E3B)",
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 8,
              }}
            >
              <FiTruck size={14} /> Vendor / Supplier Information
            </div>

            <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--navy)", marginBottom: 4 }}>
              {bill.vendor_name || "—"}
            </div>

            <div style={{ fontSize: "0.82rem", color: "var(--slate-600)", lineHeight: "1.45" }}>
              {bill.vendor_contact_person && (
                <div>
                  <strong style={{ color: "var(--slate-700)" }}>Contact Person:</strong> {bill.vendor_contact_person}
                </div>
              )}
              {bill.vendor_contact && (
                <div>
                  <strong style={{ color: "var(--slate-700)" }}>Phone:</strong> {bill.vendor_contact}
                </div>
              )}
              {bill.vendor_email && (
                <div>
                  <strong style={{ color: "var(--slate-700)" }}>Email:</strong> {bill.vendor_email}
                </div>
              )}
              {bill.vendor_ntn && (
                <div>
                  <strong style={{ color: "var(--slate-700)" }}>NTN / Tax ID:</strong>{" "}
                  <code style={{ color: "var(--navy)", fontWeight: 700 }}>{bill.vendor_ntn}</code>
                </div>
              )}
              {bill.vendor_address && (
                <div>
                  <strong style={{ color: "var(--slate-700)" }}>Address:</strong> {bill.vendor_address}
                  {bill.vendor_city ? `, ${bill.vendor_city}` : ""}
                </div>
              )}
            </div>
          </div>

          {/* Card 2: Invoice & Transaction Details */}
          <div
            style={{
              padding: "18px 20px",
              borderRadius: 10,
              backgroundColor: "#F8FAFC",
              border: "1px solid var(--slate-200)",
            }}
          >
            <div
              style={{
                fontSize: "0.72rem",
                fontWeight: 800,
                color: "#1D4ED8",
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 8,
              }}
            >
              <FiFileText size={14} /> Bill & Transaction Details
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: "0.82rem" }}>
              <div>
                <span style={{ color: "var(--slate-500)", display: "block", fontSize: "0.72rem", fontWeight: 700 }}>
                  PURCHASE DATE
                </span>
                <span style={{ fontWeight: 800, color: "var(--navy)", fontSize: "0.88rem" }}>
                  {formatDateDisplay(bill.purchase_date || bill.created_at)}
                </span>
              </div>

              <div>
                <span style={{ color: "var(--slate-500)", display: "block", fontSize: "0.72rem", fontWeight: 700 }}>
                  RECORDED BY
                </span>
                <span style={{ fontWeight: 700, color: "var(--navy)" }}>
                  {bill.created_by_name || bill.created_by_username || "Authorized Staff"}
                </span>
              </div>

              <div>
                <span style={{ color: "var(--slate-500)", display: "block", fontSize: "0.72rem", fontWeight: 700 }}>
                  TOTAL ITEMS PURCHASED
                </span>
                <span style={{ fontWeight: 800, color: "var(--primary)", fontSize: "0.92rem" }}>
                  {withReturn ? summaryTotals.totalPurchasedUnits : activeUnits} {activeUnits === 1 ? "Unit" : "Units"}
                  {withReturn && summaryTotals.totalReturnedUnits > 0 && (
                    <>
                      <span style={{ fontSize: "0.74rem", fontWeight: 700, color: "#DC2626", marginLeft: 6 }}>
                        (-{summaryTotals.totalReturnedUnits} Ret)
                      </span>
                      <span style={{ fontSize: "0.74rem", fontWeight: 800, color: "#15803D", marginLeft: 6 }}>
                        ({summaryTotals.totalRemainingUnits} Active)
                      </span>
                    </>
                  )}
                  <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--slate-500)", marginLeft: 6 }}>
                    ({consolidatedItems.length} {consolidatedItems.length === 1 ? "Line Item" : "Line Items"})
                  </span>
                </span>
              </div>

              <div>
                <span style={{ color: "var(--slate-500)", display: "block", fontSize: "0.72rem", fontWeight: 700 }}>
                  INVENTORY STATUS
                </span>
                <span style={{ fontWeight: 700, color: "#15803D" }}>
                  Active in Inventory
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ITEMIZED TABLE */}
        <div style={{ marginBottom: 28, overflowX: "auto" }}>
          <div
            style={{
              fontSize: "0.88rem",
              fontWeight: 800,
              color: "var(--navy)",
              marginBottom: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <FiBox size={16} style={{ color: "var(--primary)" }} />
              <span>Purchased Line Items ({billItems.length})</span>
            </div>
            <span style={{ fontSize: "0.78rem", color: "var(--slate-500)", fontWeight: 500 }}>
              All serial numbers registered into stock
            </span>
          </div>

          <table
            className="invoice-table"
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid var(--slate-200)",
              fontSize: "0.83rem",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#F1F5F9", borderBottom: "1.5px solid var(--slate-300)", textAlign: "left" }}>
                <th style={{ padding: "10px 10px", width: 36, color: "var(--slate-600)", fontWeight: 700, textAlign: "center" }}>#</th>
                <th style={{ padding: "10px 10px", color: "var(--slate-700)", fontWeight: 700 }}>Item Description</th>
                <th style={{ padding: "10px 10px", color: "var(--slate-700)", fontWeight: 700 }}>Category & Type</th>
                <th style={{ padding: "10px 10px", color: "var(--slate-700)", fontWeight: 700 }}>Serial Number</th>
                <th style={{ padding: "10px 10px", color: "var(--slate-700)", fontWeight: 700 }}>Part # / SKU</th>
                <th style={{ padding: "10px 10px", width: 65, color: "#065F46", fontWeight: 800, textAlign: "center" }}>Qty</th>
                <th style={{ padding: "10px 10px", width: 125, color: "var(--slate-700)", fontWeight: 700, textAlign: "right" }}>
                  Unit Cost (Rs.)
                </th>
                <th style={{ padding: "10px 10px", width: 135, color: "var(--navy)", fontWeight: 800, textAlign: "right" }}>
                  Total (Rs.)
                </th>
                <th style={{ padding: "10px 10px", minWidth: 120, color: "var(--slate-700)", fontWeight: 700 }}>Warranty Period</th>
              </tr>
            </thead>
            <tbody>
              {consolidatedItems.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: 30, textAlign: "center", color: "var(--slate-400)" }}>
                    No items found under this purchase bill.
                  </td>
                </tr>
              ) : (
                consolidatedItems.map((it, idx) => {
                  const hasReturn = withReturn && it.returnedQuantity > 0;

                  return (
                    <React.Fragment key={it.key || idx}>
                      {/* Main Purchase Line Row */}
                      <tr
                        style={{
                          borderBottom: hasReturn ? "1px dashed #CBD5E1" : "1px solid var(--slate-200)",
                          backgroundColor: idx % 2 === 0 ? "#fff" : "#FAFAFA",
                        }}
                      >
                        <td style={{ padding: "10px 10px", color: "var(--slate-400)", fontWeight: 600, textAlign: "center" }}>
                          {idx + 1}
                        </td>

                        <td style={{ padding: "10px 10px" }}>
                          <div style={{ fontWeight: 800, color: "var(--navy)", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: 6 }}>
                            <span>{it.item_name}</span>
                          </div>
                          {(!hasReturn || !withReturn) && it.soldQuantity > 0 && (
                            <div style={{ fontSize: "0.72rem", color: "var(--slate-500)", fontWeight: 600, marginTop: 2 }}>
                              <span style={{ color: "#7E22CE", fontWeight: 700 }}>{it.soldQuantity} Sold</span> •{" "}
                              <span style={{ color: "#15803D", fontWeight: 700 }}>{it.instockQuantity} In Stock</span>
                            </div>
                          )}
                        </td>

                        <td style={{ padding: "10px 10px", color: "var(--slate-600)", fontWeight: 600 }}>
                          {it.category_name}
                          {it.subcategory_name ? ` • ${it.subcategory_name}` : ""}
                        </td>

                        <td style={{ padding: "10px 10px" }}>
                          {it.serial_number ? (
                            <span
                              style={{
                                fontFamily: "monospace",
                                fontWeight: 800,
                                color: "var(--navy)",
                                backgroundColor: "#F1F5F9",
                                padding: "2px 7px",
                                borderRadius: 4,
                                border: "1px solid var(--slate-300)",
                                fontSize: "0.78rem",
                              }}
                            >
                              {it.serial_number}
                            </span>
                          ) : (
                            <span style={{ color: "var(--slate-400)", fontStyle: "italic", fontSize: "0.76rem" }}>
                              — (Bulk)
                            </span>
                          )}
                        </td>

                        <td style={{ padding: "10px 10px" }}>
                          <div style={{ fontSize: "0.76rem" }}>
                            {it.part_number && (
                              <div style={{ fontFamily: "monospace", color: "var(--slate-700)" }}>
                                PN: <strong>{it.part_number}</strong>
                              </div>
                            )}
                            {it.sku && (
                              <div style={{ fontFamily: "monospace", color: "var(--primary)" }}>
                                SKU: <strong>{it.sku}</strong>
                              </div>
                            )}
                            {!it.part_number && !it.sku && <span style={{ color: "var(--slate-400)" }}>—</span>}
                          </div>
                        </td>

                        {/* Qty Column */}
                        <td style={{ padding: "10px 10px", textAlign: "center" }}>
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "3px 9px",
                              borderRadius: 6,
                              backgroundColor: hasReturn ? "#EFF6FF" : it.originalPurchasedQty > 1 ? "#ECFDF5" : "#EFF6FF",
                              color: hasReturn ? "#1D4ED8" : it.originalPurchasedQty > 1 ? "#065F46" : "#1D4ED8",
                              fontWeight: 800,
                              fontSize: "0.85rem",
                              border: hasReturn ? "1px solid #BFDBFE" : it.originalPurchasedQty > 1 ? "1px solid #A7F3D0" : "1px solid #BFDBFE",
                            }}
                            title={hasReturn ? "Original Purchased Quantity" : undefined}
                          >
                            {it.originalPurchasedQty}
                          </span>
                        </td>

                        {/* Unit Price (PKR) Column */}
                        <td style={{ padding: "10px 10px", textAlign: "right" }}>
                          <span style={{ fontWeight: 700, color: "var(--slate-700)", fontSize: "0.84rem" }}>
                            Rs. {it.unit_price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </td>

                        {/* Total (PKR) Column */}
                        <td style={{ padding: "10px 10px", textAlign: "right" }}>
                          <span style={{ fontWeight: 800, color: "var(--navy)", fontSize: "0.88rem" }}>
                            Rs. {it.originalGrossAmt.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </td>

                        {/* Warranty Period */}
                        <td style={{ padding: "10px 10px" }}>
                          {it.warranty_start_date || it.warranty_end_date ? (
                            <div>
                              <div style={{ fontSize: "0.75rem", color: "var(--slate-600)", fontWeight: 600 }}>
                                {formatDateDisplay(it.warranty_start_date)} → {formatDateDisplay(it.warranty_end_date)}
                              </div>
                              {it.warranty_status && (
                                <span
                                  style={{
                                    fontSize: "0.66rem",
                                    fontWeight: 800,
                                    padding: "1px 6px",
                                    borderRadius: 4,
                                    backgroundColor:
                                      it.warranty_status === "Active"
                                        ? "#DCFCE7"
                                        : it.warranty_status === "Expiring Soon"
                                          ? "#FEF3C7"
                                          : "#FEE2E2",
                                    color:
                                      it.warranty_status === "Active"
                                        ? "#15803D"
                                        : it.warranty_status === "Expiring Soon"
                                          ? "#B45309"
                                          : "#B91C1C",
                                    display: "inline-block",
                                    marginTop: 2,
                                  }}
                                >
                                  {it.warranty_status}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: "var(--slate-400)", fontSize: "0.76rem" }}>No Warranty</span>
                          )}
                        </td>
                      </tr>

                      {/* Next Row: Returned to Vendor (Only when withReturn is true and hasReturn is true) */}
                      {hasReturn && (
                        <tr style={{ backgroundColor: "#FEF2F2", borderBottom: "1px dashed #FECACA" }}>
                          <td></td>
                          <td colSpan={4} style={{ padding: "8px 12px" }}>
                            <div style={{ color: "#DC2626", fontSize: "0.80rem", fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                              <FiRotateCcw size={13} style={{ flexShrink: 0 }} />
                              <span>
                                Returned to Vendor
                                {it.returnsList && it.returnsList.length > 0 && (
                                  <span style={{ fontFamily: "monospace", fontSize: "0.75rem", color: "#B91C1C", marginLeft: 6 }}>
                                    ({it.returnsList.map((r) => (r.return_number ? `#${r.return_number}` : "")).filter(Boolean).join(", ")})
                                  </span>
                                )}
                                {it.returnsList?.[0]?.return_reason && (
                                  <span style={{ fontStyle: "italic", color: "#991B1B", fontWeight: 500, marginLeft: 6 }}>
                                    — Reason: {it.returnsList[0].return_reason}
                                  </span>
                                )}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: "8px 10px", textAlign: "center" }}>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "2px 8px",
                                borderRadius: 6,
                                backgroundColor: "#FEE2E2",
                                color: "#DC2626",
                                fontWeight: 800,
                                fontSize: "0.82rem",
                                border: "1px solid #FECACA",
                              }}
                            >
                              -{it.returnedQuantity} Ret
                            </span>
                          </td>
                          <td style={{ padding: "8px 10px", textAlign: "right", color: "#991B1B", fontSize: "0.80rem", fontWeight: 600 }}>
                            Rs. {it.unit_price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td style={{ padding: "8px 10px", textAlign: "right", color: "#DC2626", fontWeight: 800, fontSize: "0.86rem" }}>
                            - Rs. {it.returnedAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td style={{ padding: "8px 10px", color: "#991B1B", fontSize: "0.74rem", fontStyle: "italic" }}>
                            Returned
                          </td>
                        </tr>
                      )}

                      {/* Then Row: Net Remaining Purchased (Only when withReturn is true and hasReturn is true) */}
                      {hasReturn && (
                        <tr style={{ backgroundColor: "#F0FDF4", borderBottom: "1.5px solid var(--slate-300)" }}>
                          <td></td>
                          <td colSpan={4} style={{ padding: "8px 12px" }}>
                            <div style={{ color: "#15803D", fontSize: "0.82rem", fontWeight: 800, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <FiCheckCircle size={13} style={{ flexShrink: 0 }} />
                              <span>Net Remaining Purchased</span>
                              {it.soldQuantity > 0 && (
                                <span style={{ fontSize: "0.72rem", color: "var(--slate-600)", fontWeight: 600, backgroundColor: "#DCFCE7", padding: "1px 7px", borderRadius: 10 }}>
                                  {it.soldQuantity} Sold • {it.instockQuantity} In Stock
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: "8px 10px", textAlign: "center" }}>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "3px 9px",
                                borderRadius: 6,
                                backgroundColor: "#DCFCE7",
                                color: "#15803D",
                                fontWeight: 800,
                                fontSize: "0.85rem",
                                border: "1px solid #BBF7D0",
                              }}
                            >
                              {it.remainingQty}
                            </span>
                          </td>
                          <td style={{ padding: "8px 10px", textAlign: "right", color: "#15803D", fontSize: "0.80rem", fontWeight: 600 }}>
                            Rs. {it.unit_price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td style={{ padding: "8px 10px", textAlign: "right", color: "#15803D", fontWeight: 900, fontSize: "0.88rem" }}>
                            Rs. {it.remainingAmt.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td style={{ padding: "8px 10px", color: "#15803D", fontSize: "0.74rem", fontWeight: 700 }}>
                            Active
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: "#F8FAFC", borderTop: "2px solid var(--slate-300)" }}>
                <td colSpan={5} style={{ padding: "12px 10px", fontWeight: 800, color: "var(--navy)" }}>
                  {withReturn && summaryTotals.totalReturnedUnits > 0 ? "TOTAL SUMMARY (NET ACTIVE)" : "GRAND TOTAL"}
                </td>
                <td style={{ textAlign: "center", padding: "12px 10px", fontWeight: 900, color: "#065F46", fontSize: "0.95rem" }}>
                  {activeUnits} {activeUnits === 1 ? "Unit" : "Units"}
                </td>
                <td></td>
                <td style={{ textAlign: "right", padding: "12px 10px", fontWeight: 900, color: "#15803D", fontSize: "1.05rem" }}>
                  Rs. {activeTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* SUMMARY / TOTAL SECTION */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 20,
            marginBottom: 32,
            paddingTop: 12,
            borderTop: "1.5px solid var(--slate-200)",
          }}
        >
          {/* Amount In Words & Notes */}
          <div style={{ maxWidth: 480, flex: 1 }}>
            <div style={{ fontSize: "0.74rem", color: "var(--slate-500)", fontWeight: 700, textTransform: "uppercase" }}>
              Amount Chargeable in Words
            </div>
            <div
              style={{
                fontSize: "0.92rem",
                fontWeight: 800,
                color: "var(--navy)",
                fontStyle: "italic",
                marginTop: 2,
                marginBottom: 14,
              }}
            >
              {numberToWords(activeTotal)}
            </div>

            <div
              style={{
                fontSize: "0.76rem",
                color: "var(--slate-500)",
                backgroundColor: "#F8FAFC",
                padding: "10px 14px",
                borderRadius: 8,
                border: "1px solid var(--slate-200)",
                lineHeight: "1.5",
              }}
            >
              <strong>Receipt Notice:</strong> All items listed above have been physically inspected, serial numbers verified,
              and recorded directly into the company active inventory database.
            </div>
          </div>

          {/* Numerical Totals Card */}
          <div
            style={{
              minWidth: 280,
              backgroundColor: "#F8FAFC",
              borderRadius: 10,
              border: "1px solid var(--slate-200)",
              padding: "16px 20px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.84rem" }}>
              <span style={{ color: "var(--slate-600)" }}>Total Quantity Purchased:</span>
              <strong style={{ color: "var(--navy)" }}>{summaryTotals.totalPurchasedUnits} Units</strong>
            </div>

            {withReturn && summaryTotals.totalReturnedUnits > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.84rem", color: "#DC2626" }}>
                <span>Returned Quantity:</span>
                <strong>- {summaryTotals.totalReturnedUnits} Units</strong>
              </div>
            )}

            {withReturn && summaryTotals.totalReturnedUnits > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.84rem", color: "#15803D" }}>
                <span>Net Active Quantity:</span>
                <strong>{summaryTotals.totalRemainingUnits} Units</strong>
              </div>
            )}

            {withReturn && summaryTotals.totalReturnedAmount > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.84rem" }}>
                <span style={{ color: "var(--slate-600)" }}>Original Gross Total:</span>
                <span style={{ color: "var(--slate-500)", textDecoration: "line-through" }}>
                  Rs. {summaryTotals.totalPurchasedAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}

            {withReturn && summaryTotals.totalReturnedAmount > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.84rem", color: "#DC2626" }}>
                <span>Returned to Vendor:</span>
                <strong>
                  - Rs. {summaryTotals.totalReturnedAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </strong>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.84rem" }}>
              <span style={{ color: "var(--slate-600)" }}>
                {withReturn && summaryTotals.totalReturnedAmount > 0 ? "Subtotal (Active Items):" : "Subtotal:"}
              </span>
              <strong style={{ color: "var(--navy)" }}>
                Rs. {activeTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </strong>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, fontSize: "0.84rem" }}>
              <span style={{ color: "var(--slate-600)" }}>Taxes & Shipping:</span>
              <span style={{ color: "var(--slate-500)", fontStyle: "italic" }}>Included</span>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                paddingTop: 10,
                borderTop: "2px solid var(--slate-300)",
                fontSize: "1.1rem",
                fontWeight: 900,
                color: "#15803D",
              }}
            >
              <span>{withReturn && summaryTotals.totalReturnedUnits > 0 ? "Net Total:" : "Total Amount:"}</span>
              <span>Rs. {activeTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>

        {/* SIGNATURES SECTION */}
        <div
          className="print-signature-section"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 24,
            paddingTop: 40,
            marginTop: 20,
            borderTop: "1px dashed var(--slate-300)",
            textAlign: "center",
          }}
        >
          <div>
            <div style={{ borderBottom: "1px solid var(--slate-400)", height: 35, marginBottom: 8 }}></div>
            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--navy)" }}>Received / Inspected By</div>
            <div style={{ fontSize: "0.72rem", color: "var(--slate-500)" }}>
              {bill.created_by_name || "Warehouse Officer"}
            </div>
          </div>

          <div>
            <div style={{ borderBottom: "1px solid var(--slate-400)", height: 35, marginBottom: 8 }}></div>
            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--navy)" }}>Authorized Signatory</div>
            <div style={{ fontSize: "0.72rem", color: "var(--slate-500)" }}>Commercial-IT Management</div>
          </div>

          <div>
            <div style={{ borderBottom: "1px solid var(--slate-400)", height: 35, marginBottom: 8 }}></div>
            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--navy)" }}>Vendor / Supplier Stamp</div>
            <div style={{ fontSize: "0.72rem", color: "var(--slate-500)" }}>{bill.vendor_name || "Supplier"}</div>
          </div>
        </div>
      </div>
    );
  };

  // =========================================================================
  // OPTION 2: DC INVOICE (Goods Receiving Challan - Commercial-IT Brand Format)
  // Without Return: Original Inward Delivery Challan slip
  // =========================================================================
  const renderDcPurchaseInvoiceTemplate = (withReturn = false) => {
    const activeTotal = withReturn ? summaryTotals.totalRemainingAmount : summaryTotals.totalPurchasedAmount;
    const activeUnits = withReturn ? summaryTotals.totalRemainingUnits : summaryTotals.totalPurchasedUnits;

    return (
      <div
        className="invoice-paper"
        style={{
          maxWidth: 1000,
          margin: "0 auto",
          backgroundColor: "#fff",
          borderRadius: 14,
          border: "1px solid var(--slate-200)",
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
          padding: "40px 48px",
          color: "var(--slate-800)",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            borderBottom: "2.5px solid var(--primary, #064E3B)",
            paddingBottom: 22,
            marginBottom: 24,
            gap: 20,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img
                src={logoImg}
                alt="Commercial IT Logo"
                style={{
                  height: 60,
                  maxHeight: 60,
                  width: "auto",
                  objectFit: "contain",
                  borderRadius: 6,
                }}
              />

            </div>

          </div>

          <div style={{ textAlign: "right", minWidth: 220 }}>
            <div
              style={{
                fontSize: "1.35rem",
                fontWeight: 900,
                color: "var(--navy)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              BILL# {bill.bill_number}
            </div>


          </div>
        </div>

        {/* METADATA CARDS: VENDOR PROFILE & INWARD DETAILS */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 20,
            marginBottom: 24,
          }}
        >
          {/* VENDOR PROFILE */}
          <div
            style={{
              backgroundColor: "#F8FAFC",
              borderRadius: 10,
              border: "1px solid var(--slate-200)",
              padding: "18px 20px",
            }}
          >
            <div
              style={{
                fontSize: "0.74rem",
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "var(--primary)",
                marginBottom: 10,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <FiTruck size={14} /> Vendor / Supplier Details & Origin
            </div>
            <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "var(--navy)", marginBottom: 4 }}>
              {bill.vendor_name || "Direct Supplier / Vendor"}
            </div>

            <div style={{ fontSize: "0.82rem", color: "var(--slate-600)", lineHeight: "1.6" }}>
              {bill.vendor_contact_person && (
                <div>
                  <strong>Contact Person:</strong> {bill.vendor_contact_person}
                </div>
              )}
              {bill.vendor_contact && (
                <div>
                  <strong>Phone / Mobile:</strong> {bill.vendor_contact}
                </div>
              )}
              {bill.vendor_email && (
                <div>
                  <strong>Email:</strong> {bill.vendor_email}
                </div>
              )}
              {bill.vendor_ntn && (
                <div>
                  <strong>NTN / Tax ID:</strong> {bill.vendor_ntn}
                </div>
              )}
              {bill.vendor_address && (
                <div>
                  <strong>Supplier Address:</strong> {bill.vendor_address}
                  {bill.vendor_city ? `, ${bill.vendor_city}` : ""}
                </div>
              )}
            </div>
          </div>

          {/* INWARD & RECEIVING RECORD */}
          <div
            style={{
              backgroundColor: "#F8FAFC",
              borderRadius: 10,
              border: "1px solid var(--slate-200)",
              padding: "18px 20px",
            }}
          >
            <div
              style={{
                fontSize: "0.74rem",
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "var(--navy)",
                marginBottom: 10,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <FiFileText size={14} /> Inward & Goods Receiving Record
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px 14px",
                fontSize: "0.82rem",
              }}
            >
              <div>
                <span style={{ color: "var(--slate-500)", display: "block", fontSize: "0.72rem", fontWeight: 700 }}>
                  BILL REF#
                </span>
                <span style={{ fontWeight: 800, color: "var(--navy)", fontFamily: "monospace" }}>
                  #{bill.bill_number}
                </span>
              </div>
              <div>
                <span style={{ color: "var(--slate-500)", display: "block", fontSize: "0.72rem", fontWeight: 700 }}>
                  RECEIVING DATE
                </span>
                <span style={{ fontWeight: 700, color: "var(--navy)" }}>{formatDateDisplay(bill.purchase_date || bill.created_at)}</span>
              </div>
              <div>
                <span style={{ color: "var(--slate-500)", display: "block", fontSize: "0.72rem", fontWeight: 700 }}>
                  INWARD OFFICER
                </span>
                <span style={{ fontWeight: 700, color: "var(--navy)" }}>
                  {bill.created_by_name || bill.created_by_username || "Authorized Staff"}
                </span>
              </div>
              <div>
                <span style={{ color: "var(--slate-500)", display: "block", fontSize: "0.72rem", fontWeight: 700 }}>
                  TOTAL UNITS
                </span>
                <span style={{ fontWeight: 800, color: "var(--primary)", fontSize: "0.92rem" }}>
                  {activeUnits} {activeUnits === 1 ? "Unit" : "Units"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ITEMS & SERIALS TABLE */}
        <div style={{ marginBottom: 24, overflowX: "auto" }}>
          <div style={{ fontWeight: 800, fontSize: "0.92rem", color: "var(--navy)", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <FiTruck size={15} style={{ color: "var(--primary)" }} /> Received Items & Serial Numbers
          </div>
          <table
            className="invoice-table"
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid var(--slate-200)",
              fontSize: "0.83rem",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#F1F5F9", borderBottom: "1.5px solid var(--slate-300)", textAlign: "left" }}>
                <th style={{ padding: "9px 12px", width: 40, color: "var(--slate-600)", fontWeight: 700, textAlign: "center" }}>Sr.#</th>
                <th style={{ padding: "9px 12px", width: 140, color: "var(--slate-700)", fontWeight: 700 }}>Category</th>
                <th style={{ padding: "9px 12px", color: "var(--slate-700)", fontWeight: 700 }}>Descriptions of Goods</th>
                <th style={{ padding: "9px 12px", width: 80, color: "var(--slate-700)", fontWeight: 700, textAlign: "center" }}>Qty.</th>
                <th style={{ padding: "9px 12px", width: 130, color: "var(--slate-700)", fontWeight: 700, textAlign: "right" }}>
                  Amount (Rs.)
                </th>
              </tr>
            </thead>
            <tbody>
              {dcInvoiceItems.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: 25, textAlign: "center", color: "var(--slate-400)" }}>
                    No items found under this purchase bill.
                  </td>
                </tr>
              ) : (
                dcInvoiceItems.map((grp, idx) => {
                  const displaySerials = grp.serials;

                  return (
                    <React.Fragment key={idx}>
                      <tr
                        style={{
                          borderBottom: "1px solid #E2E8F0",
                          backgroundColor: idx % 2 === 0 ? "#fff" : "#FAFAFA",
                        }}
                      >
                        <td style={{ padding: "10px 12px", color: "var(--slate-500)", fontWeight: 700, textAlign: "center" }}>
                          {idx + 1}
                        </td>
                        <td style={{ padding: "10px 12px", color: "var(--slate-700)", fontWeight: 600 }}>
                          {grp.categoryName}
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          <div style={{ fontWeight: 800, color: "var(--navy)", fontSize: "0.86rem" }}>
                            {grp.itemName}
                          </div>
                          {grp.partNumber && (
                            <div style={{ fontSize: "0.75rem", color: "var(--slate-500)", fontFamily: "monospace", marginTop: 2 }}>
                              P/N: <strong>{grp.partNumber}</strong>
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "center" }}>
                          <span
                            style={{
                              fontWeight: 800,
                              padding: "3px 9px",
                              borderRadius: 6,
                              backgroundColor: "#EEF2FF",
                              color: "#4338CA",
                              fontSize: "0.83rem",
                            }}
                          >
                            {grp.originalQty}
                          </span>
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right" }}>
                          <span style={{ fontWeight: 800, color: "var(--navy)", fontSize: "0.88rem" }}>
                            Rs. {formatRs(grp.originalAmount)}
                          </span>
                        </td>
                      </tr>

                      {/* Serial numbers row directly underneath */}
                      <tr style={{ backgroundColor: "#F8FAFC", borderBottom: "1.5px solid var(--slate-200)" }}>
                        <td colSpan={5} style={{ padding: "8px 14px 10px 14px" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
                            <span
                              style={{
                                fontSize: "0.74rem",
                                fontWeight: 800,
                                color: "var(--primary)",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                whiteSpace: "nowrap",
                                textTransform: "uppercase",
                                letterSpacing: "0.3px",
                                backgroundColor: "#DCFCE7",
                                padding: "2px 8px",
                                borderRadius: 4,
                              }}
                            >
                              <FiTag size={12} /> Serial Numbers ({displaySerials.length}):
                            </span>
                            <div
                              style={{
                                fontFamily: "monospace",
                                fontSize: "0.78rem",
                                color: "var(--navy)",
                                fontWeight: 600,
                                lineHeight: 1.6,
                                wordBreak: "break-word",
                                flex: 1,
                              }}
                            >
                              {displaySerials && displaySerials.length > 0 ? (
                                displaySerials.join(", ")
                              ) : (
                                <span style={{ color: "var(--slate-400)", fontStyle: "italic" }}>
                                  — No individual serial numbers (Bulk item) —
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: "#F8FAFC", borderTop: "2px solid var(--slate-300)" }}>
                <td colSpan={3} style={{ padding: "10px 12px", fontWeight: 800, color: "var(--navy)" }}>
                  Total Summary
                </td>
                <td style={{ textAlign: "center", padding: "10px 12px", fontWeight: 900, color: "#4338CA", fontSize: "0.92rem" }}>
                  {activeUnits} Units
                </td>
                <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: 900, color: "#15803D", fontSize: "0.98rem" }}>
                  Rs. {formatRs(activeTotal)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* SUMMARY / TOTAL AMOUNT SECTION */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 20,
            marginBottom: 28,
            paddingTop: 10,
            borderTop: "1.5px solid var(--slate-200)",
          }}
        >
          <div style={{ maxWidth: 480, flex: 1 }}>
            <div style={{ fontSize: "0.74rem", color: "var(--slate-500)", fontWeight: 700, textTransform: "uppercase" }}>
              Total Amount in Words
            </div>
            <div
              style={{
                fontSize: "0.92rem",
                fontWeight: 800,
                color: "var(--navy)",
                fontStyle: "italic",
                marginTop: 2,
                marginBottom: 12,
              }}
            >
              {numberToWords(activeTotal)}
            </div>

            <div
              style={{
                fontSize: "0.76rem",
                color: "var(--slate-500)",
                backgroundColor: "#F8FAFC",
                padding: "10px 14px",
                borderRadius: 8,
                border: "1px solid var(--slate-200)",
                lineHeight: "1.5",
              }}
            >
              <strong>Goods Receiving Verification:</strong> All listed items and quantities have been physically inspected, verified and formally received into inventory.
            </div>
          </div>

          <div
            style={{
              minWidth: 280,
              backgroundColor: "#F8FAFC",
              borderRadius: 10,
              border: "1px solid var(--slate-200)",
              padding: "16px 20px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.84rem" }}>
              <span style={{ color: "var(--slate-600)" }}>Total Quantity Received:</span>
              <strong style={{ color: "var(--navy)" }}>{activeUnits} Units</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.84rem" }}>
              <span style={{ color: "var(--slate-600)" }}>Item Types / Groups:</span>
              <strong style={{ color: "var(--navy)" }}>{dcInvoiceItems.length} Groups</strong>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                paddingTop: 10,
                borderTop: "2px solid var(--slate-300)",
                fontSize: "1.1rem",
                fontWeight: 900,
                color: "#15803D",
              }}
            >
              <span>Total Amount:</span>
              <span>Rs. {formatRs(activeTotal)}</span>
            </div>
          </div>
        </div>

        {/* SIGNATURES */}
        <div
          className="print-signature-section"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 24,
            paddingTop: 36,
            marginTop: 24,
            borderTop: "1px dashed var(--slate-300)",
            textAlign: "center",
          }}
        >
          <div>
            <div style={{ borderBottom: "1px solid var(--slate-400)", height: 32, marginBottom: 6 }}></div>
            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--navy)" }}>Received / Inspected By</div>
            <div style={{ fontSize: "0.72rem", color: "var(--slate-500)" }}>
              {bill.created_by_name || bill.created_by_username || "Authorized Staff"}
            </div>
          </div>
          <div>
            <div style={{ borderBottom: "1px solid var(--slate-400)", height: 32, marginBottom: 6 }}></div>
            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--navy)" }}>Authorized Signatory</div>
            <div style={{ fontSize: "0.72rem", color: "var(--slate-500)" }}>Commercial-IT Management</div>
          </div>
          <div>
            <div style={{ borderBottom: "1px solid var(--slate-400)", height: 32, marginBottom: 6 }}></div>
            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--navy)" }}>Vendor / Supplier Stamp</div>
            <div style={{ fontSize: "0.72rem", color: "var(--slate-500)" }}>
              {bill.vendor_name || "Supplier Representative"}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="purchase-invoice-container" style={{ paddingBottom: 60 }}>
      {/* Print-specific style overrides */}
      <style>{`
        @media print {
          /* Hide all UI elements outside the printable document */
          header, aside, nav, .no-print, .no-print * {
            display: none !important;
          }
          body, html {
            background: #fff !important;
            color: #000 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          main {
            padding: 0 !important;
            margin: 0 !important;
            max-width: 100% !important;
            overflow: visible !important;
          }
          .purchase-invoice-container {
            padding: 0 !important;
            margin: 0 !important;
          }
          .invoice-paper {
            box-shadow: none !important;
            border: 1px solid #ddd !important;
            border-radius: 0 !important;
            padding: 24px !important;
            margin: 0 auto !important;
            max-width: 100% !important;
            width: 100% !important;
            page-break-after: avoid;
          }
          .invoice-table th, .invoice-table td {
            padding: 8px 10px !important;
            font-size: 11px !important;
          }
          .print-signature-section {
            display: flex !important;
            page-break-inside: avoid;
          }
          .status-badge {
            border: 1px solid #000 !important;
            color: #000 !important;
            background: transparent !important;
          }
        }
      `}</style>

      {/* TOP ACTION BAR (Hidden on print) */}
      <div
        className="no-print"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 24,
          padding: "16px 20px",
          backgroundColor: "#fff",
          borderRadius: 12,
          border: "1px solid var(--slate-200)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        }}
      >
        {/* Left Side: Back button and Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button
            type="button"
            onClick={() => navigate("/purchases?tab=history")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 16px",
              borderRadius: 8,
              border: "1.5px solid var(--slate-300)",
              backgroundColor: "#fff",
              color: "var(--navy)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--slate-100)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
          >
            <FiArrowLeft size={16} /> Back to Purchases
          </button>

          <div>
            <div style={{ fontSize: "0.76rem", color: "var(--slate-500)", fontWeight: 600 }}>
              Purchases / Invoice Details
            </div>
            <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "var(--navy)" }}>
              Bill #{bill.bill_number}
            </div>
          </div>
        </div>

        {/* Right Side: Print and Delete Action Buttons */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* Edit Invoice Button */}
          {canEdit && (
            <button
              type="button"
              onClick={() => handleEditBill(bill)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "9px 18px",
                borderRadius: 8,
                border: "1.5px solid #2563EB",
                backgroundColor: "#EFF6FF",
                color: "#1D4ED8",
                fontWeight: 700,
                fontSize: "0.86rem",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#DBEAFE")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#EFF6FF")}
            >
              <FiEdit size={16} /> Edit Bill & Account
            </button>
          )}

          {/* Print Invoice Button */}
          <button
            type="button"
            onClick={handlePrint}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "9px 18px",
              borderRadius: 8,
              border: "none",
              backgroundColor: "var(--primary, #064E3B)",
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.86rem",
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(6,78,59,0.25)",
              transition: "all 0.15s ease",
            }}
          >
            <FiPrinter size={16} /> Print / Save PDF
          </button>
        </div>
      </div>

      {/* 3-OPTION TEMPLATE SELECTOR (Detail Invoice vs DC Without Return vs With Return) */}
      <div
        className="no-print"
        style={{
          backgroundColor: "#fff",
          borderRadius: 12,
          border: "1.5px solid #E2E8F0",
          padding: "16px 20px",
          marginBottom: 24,
          boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FiLayers size={18} style={{ color: "var(--primary)" }} />
            <span style={{ fontWeight: 800, fontSize: "0.95rem", color: "var(--navy)" }}>
              Choose Purchase Invoice Format (3 Options)
            </span>
          </div>
          <span style={{ fontSize: "0.78rem", color: "var(--slate-500)", fontWeight: 600 }}>
            Switch between Detail Invoice (Itemized), DC Invoice (Without Return), and Invoice (With Return)
          </span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
          {/* Option 1: Detail Purchase Invoice (Without Return) */}
          <div
            onClick={() => handleTemplateChange("no_return")}
            style={{
              padding: "16px 18px",
              borderRadius: 10,
              border: selectedTemplate === "no_return" ? "2px solid #064E3B" : "1.5px solid #E2E8F0",
              backgroundColor: selectedTemplate === "no_return" ? "#F0FDF4" : "#F8FAFC",
              cursor: "pointer",
              transition: "all 0.15s ease",
              position: "relative",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: "#064E3B",
                    display: "inline-block",
                  }}
                />
                <span style={{ fontWeight: 800, fontSize: "0.95rem", color: "#064E3B", display: "flex", alignItems: "center", gap: 5 }}>
                  <FiFileText size={15} /> 1. Detail Invoice (Without Return)
                </span>
              </div>
              {selectedTemplate === "no_return" && (
                <span style={{ color: "#064E3B", fontWeight: 800, display: "flex", alignItems: "center", gap: 3, fontSize: "0.75rem" }}>
                  <FiCheck size={14} /> Active
                </span>
              )}
            </div>
            <p style={{ margin: 0, fontSize: "0.78rem", color: "#475569", lineHeight: 1.45 }}>
              <strong>Original Itemized Bill:</strong> Har aik item ki detail alag row mein (Individual Item Name, Serials, Part #, Qty, Rates & Gross Amount).
            </p>
          </div>

          {/* Option 2: DC Invoice (Without Return) */}
          <div
            onClick={() => handleTemplateChange("dc_no_return")}
            style={{
              padding: "16px 18px",
              borderRadius: 10,
              border: (selectedTemplate === "dc_no_return" || selectedTemplate === "dc") ? "2px solid #1E3A8A" : "1.5px solid #E2E8F0",
              backgroundColor: (selectedTemplate === "dc_no_return" || selectedTemplate === "dc") ? "#EFF6FF" : "#F8FAFC",
              cursor: "pointer",
              transition: "all 0.15s ease",
              position: "relative",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: "#1E3A8A",
                    display: "inline-block",
                  }}
                />
                <span style={{ fontWeight: 800, fontSize: "0.95rem", color: "#1E3A8A", display: "flex", alignItems: "center", gap: 5 }}>
                  <FiTruck size={15} /> 2. DC Invoice (Without Return)
                </span>
              </div>
              {(selectedTemplate === "dc_no_return" || selectedTemplate === "dc") && (
                <span style={{ color: "#1E3A8A", fontWeight: 800, display: "flex", alignItems: "center", gap: 3, fontSize: "0.75rem" }}>
                  <FiCheck size={14} /> Active
                </span>
              )}
            </div>
            <p style={{ margin: 0, fontSize: "0.78rem", color: "#475569", lineHeight: 1.45 }}>
              <strong>Delivery / Inward Challan:</strong> Items category & item name se grouped, serial numbers aik saath comma-separated aur clean receiving format.
            </p>
          </div>

          {/* Option 3: Purchase Invoice (With Return) */}
          <div
            onClick={() => handleTemplateChange("with_return")}
            style={{
              padding: "16px 18px",
              borderRadius: 10,
              border: selectedTemplate === "with_return" ? "2px solid #D97706" : "1.5px solid #E2E8F0",
              backgroundColor: selectedTemplate === "with_return" ? "#FFFBEB" : "#F8FAFC",
              cursor: "pointer",
              transition: "all 0.15s ease",
              position: "relative",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: "#D97706",
                    display: "inline-block",
                  }}
                />
                <span style={{ fontWeight: 800, fontSize: "0.95rem", color: "#B45309", display: "flex", alignItems: "center", gap: 5 }}>
                  <FiRotateCcw size={15} /> 3. Purchase Invoice (With Return)
                </span>
              </div>
              {selectedTemplate === "with_return" && (
                <span style={{ color: "#B45309", fontWeight: 800, display: "flex", alignItems: "center", gap: 3, fontSize: "0.75rem" }}>
                  <FiCheck size={14} /> Active
                </span>
              )}
            </div>
            <p style={{ margin: 0, fontSize: "0.78rem", color: "#475569", lineHeight: 1.45 }}>
              <strong>Adjusted Purchase Bill:</strong> Invoice jis mein vendor ko return huay items, unka voucher #, deduction aur net remaining inventory alag rows mein show hotay hain.
            </p>
          </div>
        </div>
      </div>

      {/* RENDER CURRENT SELECTED TEMPLATE */}
      {selectedTemplate === "dc_no_return" || selectedTemplate === "dc"
        ? renderDcPurchaseInvoiceTemplate(false)
        : selectedTemplate === "with_return"
          ? renderPurchaseInvoiceTemplate(true)
          : renderPurchaseInvoiceTemplate(false)}

      {/* CONFIRM DELETE MODAL */}
      <Modal isOpen={deleteModalOpen} onClose={() => !deleting && setDeleteModalOpen(false)} maxWidth={500}>
        <div style={{ padding: "6px 4px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                backgroundColor: "#FEE2E2",
                color: "#DC2626",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <FiAlertTriangle size={24} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 800, color: "var(--navy)" }}>
                Delete Purchase Bill & Items
              </h3>
              <p style={{ margin: "2px 0 0 0", fontSize: "0.82rem", color: "var(--slate-500)" }}>
                Permanent Removal from Inventory
              </p>
            </div>
          </div>

          <div
            style={{
              padding: "14px 16px",
              backgroundColor: "#FEF2F2",
              borderRadius: 8,
              border: "1px solid #FCA5A5",
              fontSize: "0.85rem",
              color: "#991B1B",
              lineHeight: 1.5,
              marginBottom: 18,
            }}
          >
            Are you sure you want to permanently delete <strong>Bill #{bill.bill_number}</strong>?
            <br />
            <br />
            <strong>Warning:</strong> This will delete this purchase bill record and all{" "}
            <strong>{billItems.length} inventory item(s)</strong> created with this bill.
            <br />
            <small style={{ color: "#B91C1C", display: "block", marginTop: 4 }}>
              * If any item from this bill has already been sold, deletion will be blocked by the system to maintain
              inventory integrity.
            </small>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              type="button"
              disabled={deleting}
              onClick={() => setDeleteModalOpen(false)}
              style={{
                padding: "8px 18px",
                borderRadius: 6,
                border: "1px solid var(--slate-300)",
                backgroundColor: "#fff",
                color: "var(--slate-700)",
                fontWeight: 600,
                fontSize: "0.85rem",
                cursor: deleting ? "not-allowed" : "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={deleting}
              onClick={handleDeleteBill}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 20px",
                borderRadius: 6,
                border: "none",
                backgroundColor: "#DC2626",
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.85rem",
                cursor: deleting ? "not-allowed" : "pointer",
              }}
            >
              <FiTrash2 size={15} />
              {deleting ? "Deleting..." : "Yes, Delete Bill & Items"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PurchaseInvoicePage;
